import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Icon,
  alpha,
  useTheme,
  Chip,
} from "@mui/material";
import { IChartOfAccount } from "../../../../accounting.interface";

const accountTypes = [
  {
    value: "Asset",
    label: "Assets",
    color: "#10B981",
    icon: "account_balance_wallet",
  },
  {
    value: "Liability",
    label: "Liabilities",
    color: "#EF4444",
    icon: "credit_card",
  },
  { value: "Equity", label: "Equity", color: "#3B82F6", icon: "trending_up" },
  {
    value: "Revenue",
    label: "Revenue",
    color: "#8B5CF6",
    icon: "monetization_on",
  },
  {
    value: "Expense",
    label: "Expenses",
    color: "#F59E0B",
    icon: "money_off",
  },
];

const currencies = [
  { value: "BHD", label: "BHD - Bahraini Dinar" },
  { value: "USD", label: "USD - US Dollar" },
  { value: "EUR", label: "EUR - Euro" },
  { value: "GBP", label: "GBP - British Pound" },
  { value: "SAR", label: "SAR - Saudi Riyal" },
  { value: "AED", label: "AED - UAE Dirham" },
];

interface AccountFormDialogProps {
  open: boolean;
  onClose: () => void;
  account?: IChartOfAccount;
  parentAccounts: IChartOfAccount[];
  onSave: (account: Partial<IChartOfAccount>) => void;
  isLoading?: boolean;
  isEditMode?: boolean;
  selectedLevel?: number;
  selectedParentAccountId?: string;
}

export const AccountFormDialog: React.FC<AccountFormDialogProps> = ({
  open,
  onClose,
  account,
  parentAccounts,
  onSave,
  isLoading = false,
  isEditMode = false,
  selectedLevel,
  selectedParentAccountId,
}) => {
  const theme = useTheme();
  const [formData, setFormData] = React.useState({
    accountCode: account?.accountCode || "",
    accountName: account?.accountName || "",
    accountType: account?.accountType || "",
    currency: account?.currency || "USD",
    openingBalance: account?.openingBalance || "",
    parentAccountId: account?.parentAccountId || selectedParentAccountId || "",
  });

  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [isCheckingCode, setIsCheckingCode] = React.useState(false);

  // Reset form when dialog opens/closes or account changes
  React.useEffect(() => {
    if (open) {
      setFormData({
        accountCode: account?.accountCode || "",
        accountName: account?.accountName || "",
        accountType: account?.accountType || "",
        currency: account?.currency || "USD",
        openingBalance: account?.openingBalance || "",
        parentAccountId:
          account?.parentAccountId || selectedParentAccountId || "",
      });
      setErrors({});
    }
  }, [open, account, selectedParentAccountId]);

  // Auto-set account type based on selected parent if available
  React.useEffect(() => {
    if (selectedParentAccountId && !formData.accountType) {
      const parentAccount = parentAccounts.find(
        (acc) => acc.id === selectedParentAccountId
      );
      if (parentAccount) {
        setFormData((prev) => ({
          ...prev,
          accountType: parentAccount.accountType,
        }));
      }
    }
  }, [selectedParentAccountId, parentAccounts, formData.accountType]);

  // Auto-generate account code based on parent
  React.useEffect(() => {
    if (selectedParentAccountId && !isEditMode && !formData.accountCode) {
      const parentAccount = parentAccounts.find(
        (acc) => acc.id === selectedParentAccountId
      );
      if (parentAccount) {
        const suggestedCode = generateNextAccountCode(
          parentAccount,
          parentAccounts
        );
        if (suggestedCode) {
          setFormData((prev) => ({
            ...prev,
            accountCode: suggestedCode,
          }));
        }
      }
    }
  }, [
    selectedParentAccountId,
    parentAccounts,
    isEditMode,
    formData.accountCode,
  ]);

  // Function to generate next available account code
  const generateNextAccountCode = (
    parentAccount: IChartOfAccount,
    allAccounts: IChartOfAccount[]
  ): string => {
    const parentCode = parentAccount.accountCode;

    // Get all existing child accounts of this parent
    const childAccounts = allAccounts.filter(
      (acc) => acc.parentAccountId === parentAccount.id
    );
    const existingCodes = childAccounts.map((acc) => acc.accountCode);

    // Calculate the starting number for this level
    let baseNumber = parseInt(parentCode);
    let increment = 10; // Default increment for first level children

    // Adjust increment based on depth
    if (parentCode.length === 4) {
      // Parent is like 1100
      increment = 10; // Children will be 1110, 1120, etc.
    } else if (parentCode.length === 4 && parentCode.endsWith("0")) {
      // Parent is like 1110
      increment = 1; // Children will be 1111, 1112, etc.
    }

    // Find next available code
    for (let i = 1; i <= 9; i++) {
      const nextCode = (baseNumber + increment * i).toString();
      if (!existingCodes.includes(nextCode)) {
        return nextCode;
      }
    }

    return ""; // No available codes
  };

  // Debounced account code validation
  React.useEffect(() => {
    if (!formData.accountCode || formData.accountCode.length < 3) return;

    const timeoutId = setTimeout(() => {
      setIsCheckingCode(true);
      // Simulate API call for code uniqueness check
      setTimeout(() => {
        setIsCheckingCode(false);
        // Mock validation - in real app, this would be an API call
        const isDuplicate = parentAccounts.some(
          (acc) =>
            acc.accountCode === formData.accountCode && acc.id !== account?.id
        );
        if (isDuplicate) {
          setErrors((prev) => ({
            ...prev,
            accountCode: "Account code must be unique",
          }));
        } else {
          setErrors((prev) => ({ ...prev, accountCode: "" }));
        }
      }, 500);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [formData.accountCode, parentAccounts, account?.id]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.accountCode.trim()) {
      newErrors.accountCode = "Account code is required";
    } else if (formData.accountCode.length < 3) {
      newErrors.accountCode = "Account code must be at least 3 characters";
    }

    if (!formData.accountName.trim()) {
      newErrors.accountName = "Account name is required";
    }

    if (!formData.accountType) {
      newErrors.accountType = "Account type is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    // Determine if this will be a parent account (has children) or leaf account
    const willBeParent = false; // For now, all new accounts start as leaf accounts
    const allowDirectPosting = !willBeParent; // Only leaf accounts allow direct posting

    const accountData: Partial<IChartOfAccount> = {
      accountCode: formData.accountCode.trim(),
      accountName: formData.accountName.trim(),
      accountType: formData.accountType as IChartOfAccount["accountType"],
      currency: formData.currency,
      parentAccountId: formData.parentAccountId || undefined,
      openingBalance:
        allowDirectPosting && formData.openingBalance
          ? Number(formData.openingBalance)
          : undefined,
      isParent: willBeParent,
      allowDirectPosting: allowDirectPosting,
      level: selectedLevel || (formData.parentAccountId ? 2 : 1), // Use selected level or calculate
    };

    onSave(accountData);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear parent account if account type changes
    if (field === "accountType" && formData.parentAccountId) {
      setFormData((prev) => ({ ...prev, parentAccountId: "" }));
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  // Filter parent accounts by selected account type
  const filteredParentAccounts = formData.accountType
    ? parentAccounts.filter(
        (acc) => acc.accountType === formData.accountType && acc.isParent
      )
    : [];

  // Get selected parent account info
  const selectedParentAccount = parentAccounts.find(
    (acc) => acc.id === selectedParentAccountId
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          pb: 2,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon
            sx={{
              color: "primary.main",
              fontSize: 24,
            }}
          >
            {isEditMode ? "edit" : "add"}
          </Icon>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            {isEditMode ? "Edit Account" : "Create Sub-Account"}
          </Typography>
          {selectedParentAccount && !isEditMode ? (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Adding child account under{" "}
              <Box
                component="span"
                sx={{ fontWeight: 600, color: "primary.main" }}
              >
                {selectedParentAccount.accountName} (
                {selectedParentAccount.accountCode})
              </Box>
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {isEditMode
                ? "Update the account details below"
                : "Fill in the details to create a new account"}
            </Typography>
          )}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Box component="form" onSubmit={handleSubmit}>
          {/* Enhanced Level Information */}
          {selectedLevel && selectedParentAccount && (
            <Box
              sx={{
                mb: 4,
                p: 3,
                bgcolor: alpha(theme.palette.success.main, 0.04),
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  color: "success.main",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Icon sx={{ fontSize: 20 }}>account_tree</Icon>
                Account Hierarchy
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
                <Chip
                  label={`Level ${selectedLevel}`}
                  size="medium"
                  color="primary"
                  variant="filled"
                  sx={{ fontWeight: 600 }}
                />
                <Chip
                  label={`Parent: ${selectedParentAccount.accountName}`}
                  size="medium"
                  color="success"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              <Typography
                variant="body2"
                sx={{ color: "success.dark", fontStyle: "italic" }}
              >
                Account code will be auto-generated based on parent code:{" "}
                {selectedParentAccount.accountCode}
              </Typography>
            </Box>
          )}

          <Grid container spacing={3}>
            {/* Account Code */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Account Code"
                value={formData.accountCode}
                onChange={(e) =>
                  handleInputChange("accountCode", e.target.value)
                }
                error={!!errors.accountCode}
                helperText={
                  isEditMode
                    ? "Account code cannot be changed after creation"
                    : errors.accountCode
                }
                disabled={isEditMode}
                InputProps={{
                  endAdornment: !isEditMode && isCheckingCode && (
                    <CircularProgress size={16} sx={{ ml: 1 }} />
                  ),
                }}
                sx={{
                  "& .MuiInputLabel-root": {
                    fontSize: "0.875rem",
                  },
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: isEditMode
                      ? "rgba(0, 0, 0, 0.6)"
                      : "inherit",
                  },
                }}
              />
            </Grid>

            {/* Account Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Account Name"
                value={formData.accountName}
                onChange={(e) =>
                  handleInputChange("accountName", e.target.value)
                }
                error={!!errors.accountName}
                helperText={errors.accountName}
                sx={{
                  "& .MuiInputLabel-root": {
                    fontSize: "0.875rem",
                  },
                }}
              />
            </Grid>

            {/* Account Type */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.accountType}>
                <InputLabel sx={{ fontSize: "0.875rem" }}>
                  Account Type
                </InputLabel>
                <Select
                  value={formData.accountType}
                  onChange={(e) =>
                    handleInputChange("accountType", e.target.value)
                  }
                  label="Account Type"
                  disabled={!!selectedParentAccountId} // Disable if parent is pre-selected
                  sx={{
                    "& .MuiSelect-select": {
                      fontSize: "0.875rem",
                    },
                  }}
                >
                  {accountTypes.map((type: any) => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Icon sx={{ fontSize: 18 }}>
                          {type.value === "Asset"
                            ? "account_balance_wallet"
                            : type.value === "Liability"
                            ? "credit_card"
                            : type.value === "Equity"
                            ? "trending_up"
                            : type.value === "Revenue"
                            ? "monetization_on"
                            : "money_off"}
                        </Icon>
                        {type.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {errors.accountType && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, display: "block" }}
                  >
                    {errors.accountType}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Currency */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ fontSize: "0.875rem" }}>Currency</InputLabel>
                <Select
                  value={formData.currency}
                  onChange={(e) =>
                    handleInputChange("currency", e.target.value)
                  }
                  label="Currency"
                  sx={{
                    "& .MuiSelect-select": {
                      fontSize: "0.875rem",
                    },
                  }}
                >
                  {currencies.map((currency: any) => (
                    <MenuItem key={currency.value} value={currency.value}>
                      {currency.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Parent Account */}
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.parentAccountId}>
                <InputLabel sx={{ fontSize: "0.875rem" }}>
                  Parent Account{" "}
                  {selectedParentAccountId ? "(Pre-selected)" : "(Optional)"}
                </InputLabel>
                <Select
                  value={formData.parentAccountId}
                  onChange={(e) =>
                    handleInputChange("parentAccountId", e.target.value)
                  }
                  label="Parent Account (Optional)"
                  disabled={!formData.accountType || !!selectedParentAccountId}
                  sx={{
                    "& .MuiSelect-select": {
                      fontSize: "0.875rem",
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>No parent account</em>
                  </MenuItem>
                  {filteredParentAccounts.map((parent) => (
                    <MenuItem key={parent.id} value={parent.id}>
                      {parent.accountCode} - {parent.accountName}
                    </MenuItem>
                  ))}
                </Select>
                {errors.parentAccountId && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, display: "block" }}
                  >
                    {errors.parentAccountId}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Opening Balance - Only for leaf accounts */}
            {!isEditMode && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    💡 About Account Balances
                  </Typography>
                  <Typography variant="body2">
                    • <strong>Leaf accounts</strong> (accounts without
                    sub-accounts) allow direct posting and can have opening
                    balances
                    <br />• <strong>Parent accounts</strong> show "No Direct
                    Posting" - their balance is calculated from sub-accounts
                  </Typography>
                </Alert>
                <TextField
                  fullWidth
                  label="Opening Balance (Optional - Leaf Accounts Only)"
                  type="number"
                  value={formData.openingBalance}
                  onChange={(e) =>
                    handleInputChange("openingBalance", e.target.value)
                  }
                  error={!!errors.openingBalance}
                  helperText={
                    errors.openingBalance ||
                    "This account will allow direct posting and can have an opening balance. Parent accounts are calculated automatically."
                  }
                  InputProps={{
                    startAdornment: (
                      <Typography
                        variant="body2"
                        sx={{ mr: 1, color: "text.secondary" }}
                      >
                        {formData.currency}
                      </Typography>
                    ),
                  }}
                  sx={{
                    "& .MuiInputLabel-root": {
                      fontSize: "0.875rem",
                    },
                  }}
                />
              </Grid>
            )}

            {/* Opening Balance for Edit Mode - Only if account allows direct posting */}
            {isEditMode && account?.allowDirectPosting && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Opening Balance"
                  type="number"
                  value={formData.openingBalance}
                  onChange={(e) =>
                    handleInputChange("openingBalance", e.target.value)
                  }
                  error={!!errors.openingBalance}
                  helperText={
                    account?.openingBalance !== undefined
                      ? "Opening balance is already set and cannot be changed. Use merge/split operations to adjust balances."
                      : errors.openingBalance ||
                        "Set the initial balance for this account."
                  }
                  disabled={account?.openingBalance !== undefined}
                  InputProps={{
                    startAdornment: (
                      <Typography
                        variant="body2"
                        sx={{ mr: 1, color: "text.secondary" }}
                      >
                        {formData.currency}
                      </Typography>
                    ),
                  }}
                  sx={{
                    "& .MuiInputLabel-root": {
                      fontSize: "0.875rem",
                    },
                  }}
                />
              </Grid>
            )}

            {/* No Direct Posting Info for Parent Accounts */}
            {isEditMode && !account?.allowDirectPosting && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ fontSize: "0.875rem" }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    🏗️ Parent Account - No Direct Posting
                  </Typography>
                  <Typography variant="body2">
                    This account is a parent account and does not allow direct
                    posting. Its balance is automatically calculated from the
                    sum of its sub-accounts. To record transactions, use the
                    sub-accounts instead.
                  </Typography>
                </Alert>
              </Grid>
            )}

            {/* Info Alert for Edit Mode */}
            {isEditMode && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ fontSize: "0.875rem" }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Account Modification Restrictions
                  </Typography>
                  <Typography variant="body2" component="div">
                    • Account code cannot be changed after creation
                    {account?.allowDirectPosting && (
                      <>
                        <br />• Opening balance cannot be modified after account
                        creation
                      </>
                    )}
                    <br />• Contact your administrator if changes are needed
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={isLoading}
          sx={{
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading || isCheckingCode}
          startIcon={
            isLoading ? <CircularProgress size={16} /> : <Icon>save</Icon>
          }
          sx={{
            textTransform: "none",
            fontWeight: 600,
            minWidth: 120,
          }}
        >
          {isLoading ? "Saving..." : isEditMode ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
